import {
	DriveLog,
	GPSPosition,
	ProjectTime,
	ReportDriveLog,
	ReportProjectTime,
	ReportWorkingTime,
	ValidationResult,
	WorkingTime
} from "@/shared/timr";
import {RecordedEntryStatus, RecordedEntryStatusKey} from "@/shared/timrTypes";
import {isProjectTimeTaskLocked, isValidationRelevant} from "@/shared/securityHelper";
import {parseISOZonedDateTime} from "@/shared/utils/datefunctions";

const recordedEntryStatusByKey: Record<RecordedEntryStatusKey, RecordedEntryStatus> = {
	CHANGEABLE: RecordedEntryStatus.Changeable,
	LOCKED: RecordedEntryStatus.Locked,
	APPROVED: RecordedEntryStatus.Approved,
	CLEARED: RecordedEntryStatus.Cleared,
	CLOSED: RecordedEntryStatus.Closed,
	ARCHIVED: RecordedEntryStatus.Archived
};

/**
 * Maps DTO string status keys to numeric {@link RecordedEntryStatus} used by report types and UI that expects numbers.
 */
export function toRecordedEntryStatus(status: RecordedEntryStatusKey): RecordedEntryStatus {
	return recordedEntryStatusByKey[status];
}

export function mapWorkingTimeToReportWorkingTime(workingTime: WorkingTime | null, validationResults?: ValidationResult[]): ReportWorkingTime | null {
	if (!workingTime) {
		return null;
	}
	/* overwriting properties shouldn't happen, it's because there are differences in types of WorkingTime/WorkingTimeDTO (dialog uses individual record map instead WorkingTimeDTO)
	 * todo: TIMR-7806
	 *  First step: Introduce a separate type for WorkingTime (in record/report tables a custom record map is used instead the DTO) and WorkingTimeDTO (here)
	 *  Second step: refactoring/unifying the WorkingTime type and add typing everywhere instead of any, avoid record maps in backend
	 */
	return {
		breakTime: workingTime.breakTime,
		breakTimeAutomatic: workingTime.breakTimeAutomatic,
		breakTimeManual: workingTime.breakTimeManual,
		breakTimePaid: workingTime.breakTimePaid,
		compensationDeductible: workingTime.compensationDeductible,
		customField1: workingTime.customField1,
		customField2: workingTime.customField2,
		customField3: workingTime.customField3,
		customField4: workingTime.customField4,
		customField5: workingTime.customField5,
		customField6: workingTime.customField6,
		customField7: workingTime.customField7,
		deductible: workingTime.deductible,
		description: workingTime.description,
		durationDays: workingTime.durationDays,
		durationGross: workingTime.durationGross,
		durationNet: workingTime.durationNet,
		editUnit: workingTime.editUnit,
		end: workingTime.end,
		endPlatform: workingTime.endPlatform,
		id: workingTime.id,
		lastModified: workingTime.lastModified,
		lastModifiedTimezone: parseISOZonedDateTime(workingTime.lastModified).offset,
		modifiedByUser: "",
		originalStartTime: workingTime.originalStart ?? "",
		originalStartTimeZone: parseISOZonedDateTime(workingTime.originalStart)?.offset ?? "",
		originalEndTime: workingTime.originalEnd ?? "",
		originalEndTimeZone: parseISOZonedDateTime(workingTime.originalEnd)?.offset ?? "",
		start: workingTime.start,
		startPlatform: workingTime.startPlatform,
		version: workingTime.version,
		workingTimeTypeId: workingTime.workingTimeType.id,
		changed: workingTime.changed,
		status: toRecordedEntryStatus(workingTime.status),
		workingTimeTypeName: workingTime.workingTimeType.name,
		workingTimeTypeCategory: workingTime.workingTimeType.category,
		workingTimeTypeRecordingModeUser: workingTime.workingTimeType.recordingModeUser,
		userName: workingTime.user.fullname,
		startTimezone: parseISOZonedDateTime(workingTime.start).offset,
		endTimezone: parseISOZonedDateTime(workingTime.end).offset,
		startPosition: workingTime.startPosition ?? <GPSPosition>{},
		endPosition: workingTime.endPosition ?? <GPSPosition>{},
		userId: workingTime.user.id,
		workingTimeDateSpanId: workingTime.workingTimeDateSpanId ?? null,
		hasWorkingTimeRequest: workingTime.hasWorkingTimeRequest ?? false,
		workingTimeDateSpanEditable: workingTime.workingTimeDateSpanEditable ?? null,

		// set validations if a working time item is provided (working time) other
		// todo: Remove the split of relevant/irrelevant validations by nesting and runtime type, see TIMR-9318
		validations: {
			relevant: validationResults?.filter(v => isValidationRelevant(v)) ?? [],
			irrelevant: validationResults?.filter(v => !isValidationRelevant(v)) ?? []
		}
	};
}

export function mapProjectTimeToReportProjectTime(projectTime: ProjectTime | null, validationResults?: ValidationResult[]): ReportProjectTime | null {
	if (!projectTime) {
		return null;
	}
	/* overwriting properties shouldn't happen, it's because there are differences in types of ProjectTime/ProjectTimeDTO (dialog uses individual record map instead ProjectTimeDTO)
	 * todo: TIMR-7806
	 *  First step: Introduce a separate type for ProjectTime (in record/report tables a custom record map is used instead the DTO) and ProjectTimeDTO (here)
	 *  Second step: refactoring/unifying the ProjectTime type and add typing everywhere instead of any, avoid record maps in backend
	 */
	return {
		id: projectTime.id,
		attachments: projectTime.attachmentCount,
		billable: projectTime.billable,
		breakTime: projectTime.breakTime,
		breakTimeAutomatic: projectTime.breakTimeAutomatic,
		breakTimeManual: projectTime.breakTimeManual,
		changed: projectTime.changed,
		conflicted: projectTime.conflicted,
		customField1: projectTime.customField1,
		customField2: projectTime.customField2,
		customField3: projectTime.customField3,
		customField4: projectTime.customField4,
		customField5: projectTime.customField5,
		customField6: projectTime.customField6,
		customField7: projectTime.customField7,
		customTaskField1: projectTime.task.customField1,
		customTaskField2: projectTime.task.customField2,
		customTaskField3: projectTime.task.customField3,
		description: projectTime.description,
		durationGross: projectTime.durationGross,
		durationNet: projectTime.durationNet,
		end: projectTime.end,
		endPlatform: projectTime.endPlatform,
		endPosition: projectTime.endPosition ?? <GPSPosition>{},
		endTimezone: parseISOZonedDateTime(projectTime.end).offset,
		isTaskLocked: isProjectTimeTaskLocked(projectTime),
		lastModified: projectTime.lastModified,
		lastModifiedTimezone: parseISOZonedDateTime(projectTime.lastModified).offset,
		modifiedByUser: "",
		originalEndTime: projectTime.originalEnd ?? "",
		originalEndTimeZone: parseISOZonedDateTime(projectTime.originalEnd)?.offset ?? "",
		originalStartTime: projectTime.originalStart ?? "",
		originalStartTimeZone: parseISOZonedDateTime(projectTime.originalStart)?.offset ?? "",
		start: projectTime.start,
		startPlatform: projectTime.startPlatform,
		startPosition: projectTime.startPosition ?? <GPSPosition>{},
		startTimezone: parseISOZonedDateTime(projectTime.start).offset,
		status: toRecordedEntryStatus(projectTime.status),
		taskBillable: projectTime.task.billable,
		taskBreadCrumbs: projectTime.task.breadCrumbs,
		taskDescription: projectTime.task.description,
		taskEnd: projectTime.task.end,
		taskId: projectTime.task.id,
		taskName: projectTime.task.name,
		taskStart: projectTime.task.start,
		userId: projectTime.user.id,
		userName: projectTime.user.fullname,
		validations: {
			relevant: validationResults?.filter(v => isValidationRelevant(v)) ?? [],
			irrelevant: validationResults?.filter(v => !isValidationRelevant(v)) ?? []
		},
		version: projectTime.version,
	};
}

export function mapDriveLogToReportDriveLog(driveLog: DriveLog | null): ReportDriveLog | null {
	if (!driveLog) {
		return null;
	}
	return {
		id: driveLog.id,
		version: driveLog.version,
		description: null,
		userName: driveLog.user.fullname,
		userId: driveLog.user.id,
		start: driveLog.start,
		startTimezone: parseISOZonedDateTime(driveLog.start).offset,
		end: driveLog.end,
		endTimezone: parseISOZonedDateTime(driveLog.end).offset,
		originalStartTime: driveLog.originalStart ?? "",
		originalStartTimeZone: parseISOZonedDateTime(driveLog.originalStart)?.offset ?? "",
		originalEndTime: driveLog.originalEnd ?? "",
		originalEndTimeZone: parseISOZonedDateTime(driveLog.originalEnd)?.offset ?? "",
		changed: driveLog.changed,
		startPosition: driveLog.startPosition ?? <GPSPosition>{},
		endPosition: driveLog.endPosition ?? <GPSPosition>{},
		breakTime: driveLog.breakTime,
		startPlatform: driveLog.startPlatform,
		endPlatform: driveLog.endPlatform,
		durationGross: driveLog.durationGross,
		durationNet: driveLog.durationNet,
		lastModified: driveLog.lastModified,
		lastModifiedTimezone: parseISOZonedDateTime(driveLog.lastModified).offset,
		modifiedByUser: "",
		customField1: driveLog.customField1,
		customField2: driveLog.customField2,
		customField3: driveLog.customField3,
		customField4: driveLog.customField4,
		customField5: driveLog.customField5,
		customField6: driveLog.customField6,
		customField7: driveLog.customField7,
		carId: driveLog.car?.id,
		plate: driveLog.car?.plate,
		carName: driveLog.car?.name,
		route: driveLog.route,
		purpose: driveLog.purpose,
		visited: driveLog.visited,
		startMileage: driveLog.startMileage,
		endMileage: driveLog.endMileage,
		distance: driveLog.distance,
		business: driveLog.category?.business,
		startVenue: driveLog.startVenue?.name,
		endVenue: driveLog.endVenue?.name,
		status: toRecordedEntryStatus(driveLog.status),
		hasTrack: driveLog.hasTrack,
		category: driveLog.category ? {
			id: driveLog.category.id,
			name: driveLog.category.name,
			business: driveLog.category.business,
			color: driveLog.category.color,
			archived: driveLog.category.archived
		} : null,
		conflicted: driveLog.conflicted
	};
}
